import { ARCHETYPES, type EnemyKind } from '../ai/archetypes';

/**
 * S11: the bodega hunt board (PLAN-CITY §5's S11 line: "bodega hunt board
 * (pick target -> tracked)"). Deliberately excludes `rat_king` (a roaming
 * boss-tier elite, not a fair "kill N" grind target — its own habitat has
 * `maxPopulation:1` and a 6s respawn, so a board hunt could easily demand
 * more kills than the block can realistically produce in a session) and
 * `golden_pigeon` (S11's OWN rare-roamer stub — it's the reward for a lucky
 * find, not something a board should ever assign as a guaranteed objective,
 * since its ~2.5min respawn means a hunt targeting it could sit unfinishable
 * for most of a session).
 */
export const HUNT_TARGET_KINDS: EnemyKind[] = ['rat', 'pizza_rat', 'pigeon', 'dive_pigeon', 'trash_lobber'];

export interface HuntOption {
  kind: EnemyKind;
  targetCount: number;
  rewardCoins: number;
  rewardScrap: number;
}

/**
 * One tuned option per huntable kind. Every hunt grants at least 1 scrap —
 * not just a byproduct of a lucky drop roll (droptable.ts's own natural
 * materialChance is 0 for 3 of these 5 kinds) — so the hunt-board loop is a
 * RELIABLE source of the material the crafting bench needs, not a hopeful
 * one; §6.2's "the bodega-hunt-return-craft loop feels worth repeating" gate
 * depends on that reliability. Tougher/rarer kinds (the ranged lobber, the
 * ambush diver) ask for fewer kills and pay more, mirroring droptable.ts's
 * own tier-scaled shape.
 */
const HUNT_TEMPLATES: HuntOption[] = [
  { kind: 'rat', targetCount: 5, rewardCoins: 15, rewardScrap: 1 },
  { kind: 'pizza_rat', targetCount: 4, rewardCoins: 16, rewardScrap: 1 },
  { kind: 'pigeon', targetCount: 4, rewardCoins: 16, rewardScrap: 1 },
  { kind: 'dive_pigeon', targetCount: 3, rewardCoins: 20, rewardScrap: 2 },
  { kind: 'trash_lobber', targetCount: 3, rewardCoins: 22, rewardScrap: 2 },
];

/**
 * The fixed, gentle first-ever hunt a genuinely fresh save is guided into
 * directly (main.ts's first-run flow) — deliberately not one of the
 * `generateHuntOptions` random picks, so a brand-new player's very first
 * objective is never harder or luckier than intended. Lower targetCount than
 * the regular `rat` template (3 vs 5) — a guided FIRST objective should
 * resolve faster than an ordinary board pick.
 */
export const STARTER_HUNT: HuntOption = { kind: 'rat', targetCount: 3, rewardCoins: 12, rewardScrap: 1 };

/**
 * Deliberately `Math.random()`, not the seeded RNG — matches
 * `loot/droptable.ts`'s own reasoning verbatim: this block has no seeded
 * level-regen a restart/replay must reproduce (§0.7's determinism law is
 * about REPLAYABLE sequences), so which 3 options the board offers is
 * per-visit juice, not state a replay needs to recreate.
 */
export function generateHuntOptions(count = 3): HuntOption[] {
  const pool = [...HUNT_TEMPLATES];
  const n = Math.min(count, pool.length);
  const picked: HuntOption[] = [];
  for (let i = 0; i < n; i++) {
    const idx = Math.floor(Math.random() * pool.length);
    const template = pool.splice(idx, 1)[0];
    picked.push({ ...template });
  }
  return picked;
}

export function labelFor(kind: EnemyKind): string {
  return ARCHETYPES[kind].label;
}

/**
 * PLAN-CITY3 §4 K-2: "hunt board gains a LEADS section (den rumors in plain
 * language, gated on reaching the relevant checkpoint, cleared when the den
 * clears)" — the findability package's own in-fiction breadcrumb, since a
 * TELEPORT-gated door with no visible approach otherwise has zero pointers
 * toward it at all (K-2's own diagnosis: "the Rat King is unfindable by
 * construction"). Labels hint, don't spoil, matching the new interact
 * prompts' own wording (world/interact.ts's `lair_entrance`/`sewer_entrance`).
 *
 * `requiresCheckpoint` is scoped to `world/checkpoints.ts`'s existing 6
 * waypoints — neither den sits exactly on one (no checkpoint has ever been
 * placed at the sewer trap hole's own street-level spot), so both leads gate
 * on `utility_tunnel`, the nearest existing waypoint to either entrance and
 * the one most naturally reached by a player who's actually explored north
 * of the intersection. Adding a den-specific checkpoint is out of scope for
 * this package (checkpoints.ts isn't a Wave K file) — flagged here rather
 * than silently assumed ideal.
 */
export interface DenLead {
  id: 'rat_king_lair' | 'sewer_elite';
  requiresCheckpoint: string;
  text: string;
}

export const DEN_LEADS: DenLead[] = [
  { id: 'rat_king_lair', requiresCheckpoint: 'utility_tunnel', text: 'Something chewed clean through the tunnel’s dead end.' },
  { id: 'sewer_elite', requiresCheckpoint: 'utility_tunnel', text: 'Locals say something moved into the drain near the water.' },
];
